import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import MuiAutoComplete from "components/MuiAutocomplete"
import MuiTable from "components/MuiTable"
import MuiTextField from "components/MuiTextField"
import React, { useMemo } from "react"

import { faTrashXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { EPurchaseOrderItemFields, PurchaseOrderItem } from "./Projects/useProjectBasicDetails"

interface PurchaseOrderItemsTableProps {
    items: PurchaseOrderItem[]
    onAddItem: () => void
    onRemoveItem: (index: number) => void
    onUpdateItem: (index: number, field: EPurchaseOrderItemFields, value: any) => void
    skuOptions: any[]
    hasTableError?: boolean
    getItemFieldError?: (index: number, field: EPurchaseOrderItemFields) => boolean
}

const discountTypeOptions = [
    { id: 1, title: "%" },
    { id: 2, title: "₹" },
]

const PurchaseOrderItemsTable: React.FC<PurchaseOrderItemsTableProps> = React.memo(
    ({ items, onAddItem, onRemoveItem, onUpdateItem, skuOptions, hasTableError, getItemFieldError }) => {
        const columns = useMemo(
            () => [
                {
                    id: "index" as const,
                    label: "S.No.",
                    width: 60,
                    align: "center" as const,
                },
                {
                    id: "custom" as const,
                    label: "SKU Code",
                    width: 150,
                    render: (value: any, row: PurchaseOrderItem, rowIndex: number) => (
                        <MuiAutoComplete
                            disablePortal={false}
                            size="small"
                            placeholder="Select SKU"
                            options={skuOptions}
                            value={row[EPurchaseOrderItemFields.SKU_CODE] || null}
                            onChange={value => onUpdateItem(rowIndex, EPurchaseOrderItemFields.SKU_CODE, value)}
                            error={getItemFieldError?.(rowIndex, EPurchaseOrderItemFields.SKU_CODE)}
                        />
                    ),
                },
                {
                    id: "custom" as const,
                    label: "Quantity",
                    width: 100,
                    render: (value: any, row: PurchaseOrderItem, rowIndex: number) => (
                        <MuiTextField
                            size="small"
                            type="number"
                            placeholder="0"
                            value={row[EPurchaseOrderItemFields.QUANTITY]}
                            onChange={e => onUpdateItem(rowIndex, EPurchaseOrderItemFields.QUANTITY, e.target.value)}
                            error={getItemFieldError?.(rowIndex, EPurchaseOrderItemFields.QUANTITY)}
                        />
                    ),
                },
                {
                    id: "custom" as const,
                    label: "Rate",
                    width: 100,
                    render: (value: any, row: PurchaseOrderItem, rowIndex: number) => (
                        <MuiTextField
                            size="small"
                            type="number"
                            placeholder="0"
                            value={row[EPurchaseOrderItemFields.RATE]}
                            onChange={e => onUpdateItem(rowIndex, EPurchaseOrderItemFields.RATE, e.target.value)}
                            error={getItemFieldError?.(rowIndex, EPurchaseOrderItemFields.RATE)}
                        />
                    ),
                },
                {
                    id: "custom" as const,
                    label: "Discount",
                    width: 150,
                    render: (value: any, row: PurchaseOrderItem, rowIndex: number) => (
                        <Box display="flex" gap={1}>
                            <MuiTextField
                                size="small"
                                type="number"
                                placeholder="0"
                                value={row[EPurchaseOrderItemFields.DISCOUNT_VALUE]}
                                onChange={e =>
                                    onUpdateItem(rowIndex, EPurchaseOrderItemFields.DISCOUNT_VALUE, e.target.value)
                                }
                                sx={{ flex: 1, minWidth: 60 }}
                            />
                            <MuiAutoComplete
                                disablePortal={false}
                                size="small"
                                options={discountTypeOptions}
                                value={row[EPurchaseOrderItemFields.DISCOUNT_TYPE]}
                                onChange={value =>
                                    onUpdateItem(rowIndex, EPurchaseOrderItemFields.DISCOUNT_TYPE, value)
                                }
                                sx={{ width: 50 }}
                                disableClearable
                            />
                        </Box>
                    ),
                },
                {
                    id: "custom" as const,
                    label: "Tax (%)",
                    width: 100,
                    render: (value: any, row: PurchaseOrderItem, rowIndex: number) => (
                        <MuiTextField
                            size="small"
                            type="number"
                            placeholder="0"
                            value={row[EPurchaseOrderItemFields.TAX]}
                            onChange={e => onUpdateItem(rowIndex, EPurchaseOrderItemFields.TAX, e.target.value)}
                        />
                    ),
                },
                {
                    id: EPurchaseOrderItemFields.TOTAL_AMOUNT,
                    label: "Total Amount",
                    width: 120,
                    align: "right" as const,
                },
                {
                    id: "custom" as const,
                    label: "",
                    width: 50,
                    render: (_: any, __: PurchaseOrderItem, rowIndex: number) => (
                        <Box
                            width={"20px"}
                            height={"20px"}
                            display={"flex"}
                            justifyContent={"center"}
                            borderRadius={"50%"}
                            bgcolor={items.length <= 1 ? "#E0E0E0" : "#F940541A"}
                            alignItems={"center"}
                            sx={{
                                cursor: items.length <= 1 ? "not-allowed" : "pointer",
                                opacity: items.length <= 1 ? 0.5 : 1,
                            }}
                            onClick={() => items.length > 1 && onRemoveItem(rowIndex)}>
                            <FontAwesomeIcon
                                icon={faTrashXmark}
                                color={items.length <= 1 ? "#9E9E9E" : "#F94054"}
                                size="sm"
                            />
                        </Box>
                    ),
                },
            ],
            [skuOptions, items.length, onUpdateItem, onRemoveItem, getItemFieldError]
        )

        return (
            <Box display="flex" flexDirection="column" gap={2}>
                <Typography variant="label" fontWeight="500">
                    PO Items
                </Typography>
                <Box sx={{ maxHeight: "400px", overflow: "auto" }}>
                    <MuiTable
                        columns={columns}
                        rows={items}
                        containerSx={{
                            border: hasTableError ? "1px solid #F94054" : "1px solid #e0e0e0",
                            borderRadius: "8px",
                        }}
                    />
                </Box>
                <Button variant="outlined" onClick={onAddItem} sx={{ alignSelf: "center", mt: 1 }}>
                    + Add New Item
                </Button>
            </Box>
        )
    }
)

PurchaseOrderItemsTable.displayName = "PurchaseOrderItemsTable"

export default PurchaseOrderItemsTable
